import JSZip from 'jszip';
import { saveAs } from 'file-saver';

class ExportService {
  /**
   * Get the current domain/origin for URL generation
   */
  getCurrentDomain() {
    // This will get the current domain including protocol and port
    // Works for localhost, preview domains, and production
    return window.location.origin;
  }

  /**
   * Generate the full URL for a file in workspace
   * @param {string} projectId - The project ID
   * @param {string} collectionId - The collection ID (optional for root files)
   * @param {string} fileId - The file ID
   * @returns {string} The complete URL to the file
   */
  generateFileUrl(projectId, collectionId, fileId) {
    const domain = this.getCurrentDomain();
    
    // Use the actual routing format: /project/:projectId/file/:fileId
    // This format works for both collection files and root files
    // The collection context is handled internally by the app
    return `${domain}/project/${projectId}/file/${fileId}`;
  }

  /**
   * Convert files data to CSV format
   * @param {Array} files - Array of file objects
   * @param {string} projectId - The project ID
   * @param {string} collectionId - The collection ID (optional)
   * @returns {string} CSV content as string
   */
  generateCSVContent(files, projectId, collectionId = null) {
    // CSV headers
    const headers = ['Name', 'URL', 'File Type', 'File Size (KB)', 'Created Date', 'Last Modified'];
    
    // Generate CSV rows
    const rows = files.map(file => {
      const url = this.generateFileUrl(projectId, collectionId, file._id);
      const fileSizeKB = file.fileSize ? Math.round(file.fileSize / 1024) : 0;
      const createdDate = file.createdAt ? new Date(file.createdAt).toLocaleString() : '';
      const modifiedDate = file.updatedAt ? new Date(file.updatedAt).toLocaleString() : '';
      
      // Escape special characters and wrap in quotes if needed
      const escapeCsvField = (field) => {
        if (field === null || field === undefined) return '';
        const fieldStr = String(field);
        // If field contains comma, newline, or quotes, wrap it in quotes
        if (fieldStr.includes(',') || fieldStr.includes('\n') || fieldStr.includes('"')) {
          return `"${fieldStr.replace(/"/g, '""')}"`;
        }
        return fieldStr;
      };
      
      return [
        escapeCsvField(file.originalName || file.name || 'Unnamed'),
        escapeCsvField(url),
        escapeCsvField(file.fileType || 'unknown'),
        escapeCsvField(fileSizeKB),
        escapeCsvField(createdDate),
        escapeCsvField(modifiedDate)
      ].join(',');
    });
    
    // Combine headers and rows
    return [headers.join(','), ...rows].join('\n');
  }

  /**
   * Download a single collection as CSV
   * @param {Object} collection - Collection object with files
   * @param {string} projectId - The project ID
   * @param {string} projectName - The project name
   */
  async downloadCollectionAsCSV(collection, projectId, projectName) {
    try {
      // Generate CSV content
      const csvContent = this.generateCSVContent(
        collection.files || [], 
        projectId, 
        collection._id
      );
      
      // Create blob
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `${projectName}_${collection.name}_${timestamp}.csv`
        .replace(/[^a-z0-9_\-\.]/gi, '_'); // Sanitize filename
      
      // Trigger download
      saveAs(blob, filename);
      
      return { success: true };
    } catch (error) {
      console.error('Error downloading collection as CSV:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to download collection'
      };
    }
  }

  /**
   * Download all collections in a project as a ZIP file with CSVs
   * @param {Object} project - Project object
   * @param {Array} collections - Array of collection objects with files
   * @param {Array} rootFiles - Array of root-level files (optional)
   */
  async downloadProjectCollectionsAsZip(project, collections, rootFiles = []) {
    try {
      const zip = new JSZip();
      
      // Create main folder with project name
      const folderName = `${project.name} Collections`.replace(/[^a-z0-9_\-\s]/gi, '_');
      const projectFolder = zip.folder(folderName);
      
      // Add metadata file
      const metadata = {
        projectName: project.name,
        projectId: project._id,
        exportDate: new Date().toISOString(),
        totalCollections: collections.length,
        totalRootFiles: rootFiles.length,
        exportedFrom: this.getCurrentDomain()
      };
      projectFolder.file('_metadata.json', JSON.stringify(metadata, null, 2));
      
      // Process root files if any
      if (rootFiles && rootFiles.length > 0) {
        const rootCsvContent = this.generateCSVContent(rootFiles, project._id, null);
        const rootFileName = `_root_files.csv`;
        projectFolder.file(rootFileName, rootCsvContent);
      }
      
      // Process each collection
      for (const collection of collections) {
        // Skip empty collections unless user wants them
        if (!collection.files || collection.files.length === 0) {
          // Still create an empty CSV to show the collection exists
          const emptyCsvContent = 'Name,URL,File Type,File Size (KB),Created Date,Last Modified\n';
          const csvFileName = `${collection.name}.csv`.replace(/[^a-z0-9_\-\.]/gi, '_');
          projectFolder.file(csvFileName, emptyCsvContent);
        } else {
          // Generate CSV for this collection
          const csvContent = this.generateCSVContent(
            collection.files,
            project._id,
            collection._id
          );
          
          // Sanitize collection name for filename
          const csvFileName = `${collection.name}.csv`.replace(/[^a-z0-9_\-\.]/gi, '_');
          
          // Add CSV to zip
          projectFolder.file(csvFileName, csvContent);
        }
      }
      
      // Generate the zip file
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      const zipFilename = `${project.name}_collections_export_${timestamp}.zip`
        .replace(/[^a-z0-9_\-\.]/gi, '_');
      
      // Trigger download
      saveAs(zipBlob, zipFilename);
      
      return { success: true };
    } catch (error) {
      console.error('Error downloading project collections as ZIP:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to download project collections'
      };
    }
  }

  /**
   * Download files list as CSV (for backward compatibility or simple exports)
   * @param {Array} files - Array of file objects
   * @param {string} name - Name for the CSV file
   * @param {string} projectId - The project ID
   * @param {string} collectionId - The collection ID (optional)
   */
  async downloadFilesAsCSV(files, name, projectId, collectionId = null) {
    try {
      // Generate CSV content
      const csvContent = this.generateCSVContent(files, projectId, collectionId);
      
      // Create blob
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `${name}_files_${timestamp}.csv`
        .replace(/[^a-z0-9_\-\.]/gi, '_');
      
      // Trigger download
      saveAs(blob, filename);
      
      return { success: true };
    } catch (error) {
      console.error('Error downloading files as CSV:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to download files'
      };
    }
  }
}

export default new ExportService();